import React, { useState } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import MoreIcon from '@atlaskit/icon/glyph/more';
import ModalDialog, { ModalBody, ModalTransition } from '@atlaskit/modal-dialog';
import { token } from '@atlaskit/tokens';
import { ShortcutScope } from '@atlassian/jira-common-components-keyboard-shortcuts';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl';
import { ResponsiveButton } from '@atlassian/jira-responsive-button';
import type { ProjectKey } from '@atlassian/jira-shared-types';
import FeedbackButton from '@atlassian/jira-software-deployments-feedback-button';
import { EmptyStateTutorialEntryPoint } from '@atlassian/jira-software-empty-state-experience-deployments/src/async';
import { messages } from './messages';

const MANAGE_APPS_URL = 'plugins/servlet/upm';

type Props = {
	isProjectAdmin: boolean;
	isSimplified: boolean;
	hasDeployments?: boolean;
	projectKey: ProjectKey;
	// Only for VR test to capture open state
	defaultOpen?: boolean;
};

export const HeaderMeatballMenu = ({
	isProjectAdmin,
	isSimplified,
	projectKey,
	hasDeployments = false,
	defaultOpen = false,
}: Props) => {
	const [isEmptyStateTutorialOpen, setIsEmptyStateTutorialOpen] = useState(false);

	const { formatMessage } = useIntl();
	const hasProjectKey = !!projectKey;

	const MANAGE_FEATURES_URL = `jira/software/${
		isSimplified ? '' : 'c/'
	}projects/${projectKey}/settings/features`;

	const showMenu = isProjectAdmin || hasDeployments;

	const closeEmptyStateTutorial = () => setIsEmptyStateTutorialOpen(false);

	return showMenu ? (
		<>
			<DropdownMenu
				defaultOpen={defaultOpen}
				trigger={({ triggerRef, ...props }) =>
					ff('sea-2744-jsw-tabs-nav') ? (
						<ResponsiveButton
							{...props}
							icon={MoreIcon}
							label={formatMessage(messages.buttonLabel)}
							ref={triggerRef}
						/>
					) : (
						<Button {...props} iconBefore={<MoreIcon label="More actions" />} ref={triggerRef} />
					)
				}
				placement="bottom-end"
			>
				<DropdownItemGroup>
					{isProjectAdmin ? (
						<>
							{ff('sea-2744-jsw-tabs-nav') && hasProjectKey && (
								<DropdownItem href={`/${MANAGE_FEATURES_URL}`}>
									{formatMessage(messages.manageProjectFeaturesButtonLabel)}
								</DropdownItem>
							)}
							<DropdownItem href={`/${MANAGE_APPS_URL}`}>
								{formatMessage(messages.manageInstalledAppsButtonLabel)}
							</DropdownItem>
							{!ff('sea-2744-jsw-tabs-nav') && hasProjectKey && (
								<DropdownItem href={`/${MANAGE_FEATURES_URL}`}>
									{formatMessage(messages.manageProjectFeaturesButtonLabel)}
								</DropdownItem>
							)}
						</>
					) : null}
					{hasDeployments ? (
						<DropdownItem onClick={() => setIsEmptyStateTutorialOpen(true)}>
							{formatMessage(messages.usingDeploymentsButtonLabel)}
						</DropdownItem>
					) : null}
					{ff('sea-2744-jsw-tabs-nav') && (
						<DropdownItem>
							<JSErrorBoundary
								fallback="unmount"
								packageName="jiraSoftwareDeploymentsHeaderMeatballMenu"
								teamName="fusion-solaris"
								id="feedbackButton"
							>
								<MenuButtonWrapper>
									<FeedbackButton />
								</MenuButtonWrapper>
							</JSErrorBoundary>
						</DropdownItem>
					)}
				</DropdownItemGroup>
			</DropdownMenu>
			<ModalTransition>
				{isEmptyStateTutorialOpen ? (
					<ShortcutScope>
						<ModalDialog onClose={closeEmptyStateTutorial} width={592}>
							<ModalBody>
								<ModalContent>
									<EmptyStateTutorialEntryPoint
										isOpen={isEmptyStateTutorialOpen}
										onClose={closeEmptyStateTutorial}
									/>
								</ModalContent>
							</ModalBody>
						</ModalDialog>
					</ShortcutScope>
				) : null}
			</ModalTransition>
		</>
	) : null;
};
export default HeaderMeatballMenu;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ModalContent = styled.div({
	marginLeft: token('space.negative.300', '-24px'),
	width: 'calc(100% + 48px)',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MenuButtonWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	button: {
		padding: 0,
		fontWeight: 'normal',
		'&:hover': {
			background: 'transparent',
		},
	},
});
